import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import { mediaUrl } from "../api";

const HeroSlider = () => {
  const { tr, ui } = useLang();
  const { categories } = useData();
  const [i, setI] = useState(0);

  const slides = categories.map((c) => ({
    image: c.image,
    kicker: { en: "HAAS MOSAIC", tr: "HAAS MOZAİK" },
    category: c.name,
    title: c.tagline,
    text: c.desc,
    link: `/product-category/${c.slug}`,
  }));
  const n = slides.length;

  const go = useCallback((d) => setI((p) => (n ? (p + d + n) % n : 0)), [n]);

  useEffect(() => {
    if (!n) return;
    const id = setInterval(() => setI((p) => (p + 1) % n), 6500);
    return () => clearInterval(id);
  }, [n]);

  useEffect(() => { if (i >= n && n) setI(0); }, [n, i]);

  if (!n) {
    return <section className="h-[100vh] min-h-[600px] w-full bg-[#211d18]" />;
  }

  return (
    <section className="relative h-[100vh] min-h-[600px] w-full overflow-hidden bg-[#211d18]">
      {slides.map((s, idx) => (
        <div key={idx} className={`hero-slide absolute inset-0 transition-opacity duration-[1400ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${idx === i ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}>
          <img src={mediaUrl(s.image)} alt={tr(s.title)} loading={idx === 0 ? "eager" : "lazy"} decoding="async" className={`w-full h-full object-cover ${idx === i ? "animate-kenburns" : ""}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25" />
        </div>
      ))}

      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 w-full">
          <div className="max-w-2xl" key={i}>
            <p className="animate-fade-up text-[#e2c9a8] text-[12px] md:text-[13px] uppercase tracking-luxe mb-5">{tr(slides[i].kicker)} · {tr(slides[i].category)}</p>
            <h1 className="animate-fade-up text-white font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] font-medium mb-6" style={{ animationDelay: "0.1s" }}>{tr(slides[i].title)}</h1>
            <p className="animate-fade-up text-white/85 text-[15px] md:text-[17px] leading-relaxed max-w-xl mb-9 line-clamp-3" style={{ animationDelay: "0.2s" }}>{tr(slides[i].text)}</p>
            <Link to={slides[i].link} className="animate-fade-up group inline-flex items-center gap-3 bg-[#a67c52] hover:bg-[#8f6844] text-white text-[13px] uppercase tracking-[0.16em] px-8 py-4 transition-colors" style={{ animationDelay: "0.3s" }}>
              {ui.discover} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-8 right-5 md:right-8 flex items-center gap-3">
        <button onClick={() => go(-1)} className="w-11 h-11 flex items-center justify-center border border-white/40 text-white hover:bg-white hover:text-[#211d18] transition-colors" aria-label="prev"><ChevronLeft size={20} /></button>
        <button onClick={() => go(1)} className="w-11 h-11 flex items-center justify-center border border-white/40 text-white hover:bg-white hover:text-[#211d18] transition-colors" aria-label="next"><ChevronRight size={20} /></button>
      </div>
      <div className="absolute z-20 bottom-8 left-5 md:left-8 flex items-center gap-1.5 max-w-[70%] flex-wrap">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} className={`h-[3px] transition-all duration-500 ${idx === i ? "w-10 bg-[#a67c52]" : "w-5 bg-white/50"}`} aria-label={`slide ${idx + 1}`} />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
